import { ItemsPerPage, Table } from '@billy/design-system'
import Cookies from 'js-cookie'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { Row } from 'react-table'

import { useTransactionView } from '@features/transactions/contexts/transactionViewContext'
import { TransactionSortProperty } from '@features/transactions/enums/transactionSortProperty'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../../config/queryClient'
import { CookieKeys } from '../../../../../../enums/cookieKeys'
import { CustomEvent } from '../../../../../../enums/customEvent'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { SortDirection } from '../../../../../../enums/sortDirection'
import { TableIds } from '../../../../../../enums/tableIds'
import { useCustomEventListener } from '../../../../../../hooks/useCustomEventListener'
import { getTableColumnIdDirection } from '../../../../../../utils/getTableColumnIdDirection'
import { useTransactionsListFilters } from '../../../../contexts/transactionsListFiltersContext'
import { useTransactionAttachmentPreview } from '../../hooks/useTransactionAttachmentPreview'
import { useTransactions } from '../../hooks/useTransactions'
import { useTransactionsTableData } from '../../hooks/useTransactionsTableData'
import { TransactionsTableRow } from '../../types/transactionsTableRow'
import * as Styled from './styles'
import { getTransactionTableColumns } from './utils/getTransactionTableColumns'

export const TransactionsTable = (): ReactElement => {
  const { t } = useTranslation()
  const { transactions, isLoading, pagination } = useTransactions()
  const data = useTransactionsTableData(transactions)
  const { organization } = useUserOrganization()
  const isFrenchOrganization = organization?.countryId === 'FR'
  const {
    appliedFilters: { pageSize, sortDirection, sortProperty },
    setFilters: setQueryParams,
  } = useTransactionsListFilters()

  const { handleTransactionClick, isOpen, selectedTransaction } = useTransactionView()
  const location = useLocation()

  const {
    loadingTransactionId,
    modal: attachmentPreviewModal,
    openAttachmentPreview,
  } = useTransactionAttachmentPreview()

  const columns = useMemo(
    () =>
      getTransactionTableColumns({
        attachmentPreview: { loadingTransactionId, openAttachmentPreview },
        isFrenchOrganization,
        t,
      }),
    [t, loadingTransactionId, openAttachmentPreview, isFrenchOrganization],
  )

  const sortedColumnId = useMemo(() => {
    return getTableColumnIdDirection(sortProperty, sortDirection === SortDirection.Desc)
  }, [sortDirection, sortProperty])

  const handleRowClick = useCallback(
    (row: Row<TransactionsTableRow>) => {
      Cookies.set(CookieKeys.TransactionsListQuery, location.search)
      handleTransactionClick(row.original)
    },
    [handleTransactionClick, location.search],
  )

  /* Allow user to change between transactions while staying "in the same" drawer */
  const handleTableMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('[data-row-id]')) {
      event.stopPropagation()
    }
  }, [])

  const handlePageChange = useCallback(
    (page: number) => {
      setQueryParams({ page })
    },
    [setQueryParams],
  )

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: ItemsPerPage) => {
      setQueryParams({ page: 1, pageSize: itemsPerPage })
    },
    [setQueryParams],
  )

  const handleSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      setQueryParams({
        page: 1,
        sortProperty: columnId as TransactionSortProperty,
        sortDirection: isDesc ? SortDirection.Desc : SortDirection.Asc,
      })
    },
    [setQueryParams],
  )

  // Handle ember calls success state
  const handleInvalidateTransactions = () => {
    queryClient.invalidateQueries([QueryKeys.Transactions])
  }

  useCustomEventListener(CustomEvent.PaymentSaved, handleInvalidateTransactions)
  useCustomEventListener(CustomEvent.PaymentUpdated, handleInvalidateTransactions)
  useCustomEventListener(CustomEvent.InvoiceUpdated, handleInvalidateTransactions)
  useCustomEventListener(CustomEvent.TransactionsDaybookReversed, handleInvalidateTransactions)
  useCustomEventListener(CustomEvent.TransactionsDaybookVoided, handleInvalidateTransactions)

  return (
    <Styled.TableWrapper
      onMouseDown={handleTableMouseDown}
      selectedTransactionId={isOpen ? selectedTransaction?.id : undefined}
    >
      <Table
        columns={columns}
        currentPage={pagination?.page}
        data={data}
        id={TableIds.TransactionsList}
        isLoading={isLoading}
        itemsPerPage={pageSize}
        onItemsPerPageChange={handleItemsPerPageChange}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        onSort={handleSort}
        sortedColumnId={sortedColumnId}
        totalPageItems={pagination?.total}
        withColumnsFiltering
        withItemsPerPageSelect
        withStickyHeader
      />
      {attachmentPreviewModal}
    </Styled.TableWrapper>
  )
}
